import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon } from '@heroicons/react/outline';
import { functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { SCHOOL_TYPES } from '../../constants/schools';
import { PARTICIPATING_SCHOOLS, participatingSchoolPhase } from '../../constants/pilotEligibility';

/**
 * RmsSencoRegistrationForm
 * ------------------------
 * Purpose:
 *   Dialog for RMS Org. Admins to register a SENCO (Special Educational Needs
 *   Coordinator) as a school user. Unlike parent carers (magic links), SENCOs
 *   sign in with a password because they manage several children's details
 *   (design decision, 6 Oct 2026). The backend creates the account and emails
 *   the SENCO a password-setup link.
 *
 * Captured details (requirements meeting, 6 Oct 2026):
 *   - First name, last name, email address
 *   - School name
 *   - Whether the school is primary or secondary
 *   - Explicit confirmation that this person is the school's SENCO
 *
 * Props:
 *   - isOpen: boolean - Controls dialog visibility
 *   - onClose: () => void - Callback when dialog should close
 *   - orgId: string - The organisation ID the SENCO is registered under
 *   - onSuccess: () => void - Callback after successful registration
 */

const SCHOOL_PHASE_OPTIONS = [
  { value: SCHOOL_TYPES.PRIMARY, label: 'Primary school' },
  { value: SCHOOL_TYPES.SECONDARY, label: 'Secondary school' },
];

// Keep name input to letters (any alphabet), spaces, hyphens and apostrophes.
// Unicode-aware so accented names (José, Siân, Müller) are not corrupted.
const sanitizeNameInput = (value) => value.replace(/[^\p{L}\s\-']/gu, '');

export default function RmsSencoRegistrationForm({ isOpen, onClose, orgId, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  // Form field state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [schoolPhase, setSchoolPhase] = useState('');
  const [sencoConfirmed, setSencoConfirmed] = useState(false);

  // The success auto-close timer: cleared on reopen/unmount so a stale timer
  // never closes a freshly reopened form.
  const closeTimerRef = useRef(null);
  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);
  useEffect(() => clearCloseTimer, [clearCloseTimer]);

  const resetForm = useCallback(() => {
    clearCloseTimer();
    setFirstName('');
    setLastName('');
    setEmail('');
    setSchoolName('');
    setSchoolPhase('');
    setSencoConfirmed(false);
    setSubmitError('');
    setSubmitSuccess('');
    setIsSubmitting(false);
  }, [clearCloseTimer]);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const validateEmail = useCallback((emailValue) => {
    if (!emailValue) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  }, []);

  const isFormValid = useMemo(() => {
    const isEmailValid = email ? validateEmail(email) : false;
    return (
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      isEmailValid &&
      schoolName.trim().length > 0 &&
      schoolPhase.length > 0 &&
      sencoConfirmed
    );
  }, [firstName, lastName, email, schoolName, schoolPhase, sencoConfirmed, validateEmail]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');

    try {
      const registerSchoolUserFn = httpsCallable(functions, 'rmsRegisterSchoolUser');
      const result = await registerSchoolUserFn({
        orgId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.toLowerCase().trim(),
        schoolName: schoolName.trim(),
        schoolPhase,
        sencoConfirmed: true,
      });

      if (result?.data?.success) {
        setSubmitSuccess(
          'SENCO registered. They will receive an email with a link to set up their password.'
        );

        if (onSuccess) {
          onSuccess();
        }

        clearCloseTimer();
        closeTimerRef.current = setTimeout(() => {
          closeTimerRef.current = null;
          onClose();
        }, 2000);
      } else {
        throw new Error(result?.data?.message || 'Failed to register SENCO');
      }
    } catch (error) {
      console.error('[RmsSencoRegistrationForm] Error:', error);

      if (error?.code === 'functions/already-exists' || error?.message?.includes('already exists')) {
        setSubmitError('A user with this email already exists. Please use a different email address.');
      } else if (error?.code === 'functions/invalid-argument') {
        setSubmitError(error.message || 'Invalid input. Please check your form data.');
      } else {
        setSubmitError(error?.message || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [orgId, firstName, lastName, email, schoolName, schoolPhase, onSuccess, onClose, clearCloseTimer]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
        <Dialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[85vh] w-[90vw] max-w-md bg-white rounded-xl shadow-lg z-50 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between p-5 pb-0">
            <div>
              <Dialog.Title className="text-xl font-semibold text-gray-900">
                Register SENCO
              </Dialog.Title>
              <Dialog.Description className="text-sm text-gray-500 mt-0.5">
                Create a school user account. The SENCO will receive an email to set up their password.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close dialog"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Content */}
          <div className="flex-grow overflow-y-auto p-5 space-y-4" style={{ maxHeight: '60vh' }}>
            {/* First Name */}
            <div>
              <label htmlFor="sencoFirstName" className="block text-sm font-medium text-gray-700 mb-1">
                First name <span className="text-red-500">*</span>
              </label>
              <input
                id="sencoFirstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(sanitizeNameInput(e.target.value))}
                placeholder="Enter first name"
                className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="sencoLastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last name <span className="text-red-500">*</span>
              </label>
              <input
                id="sencoLastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(sanitizeNameInput(e.target.value))}
                placeholder="Enter last name"
                className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="sencoEmail" className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="sencoEmail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                className={`w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  email && !validateEmail(email) ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {email && !validateEmail(email) && (
                <p className="text-xs text-red-500 mt-1">Please enter a valid email address</p>
              )}
            </div>

            {/* School: the pilot's list only (TDI-776, R-042), so pupils,
                invites, Admin Handover and EMIS file names all spell it as
                SOP Appendix F does. The phase comes from the list; an
                all-through school (Armfield) still asks it. */}
            <div>
              <label htmlFor="sencoSchoolName" className="block text-sm font-medium text-gray-700 mb-1">
                School <span className="text-red-500">*</span>
              </label>
              <select
                id="sencoSchoolName"
                value={schoolName}
                onChange={(e) => {
                  const name = e.target.value;
                  setSchoolName(name);
                  const listedPhase = participatingSchoolPhase(name);
                  setSchoolPhase(typeof listedPhase === 'string' ? listedPhase : '');
                }}
                className="w-full p-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Choose a school</option>
                {PARTICIPATING_SCHOOLS.map((school) => (
                  <option key={school.name} value={school.name}>{school.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-600 mt-1">
                Only schools taking part in the pilot are listed. If a school is missing, contact pilot support.
              </p>
            </div>

            {/* School phase: asked only for an all-through school, where the
                list cannot answer it. */}
            {participatingSchoolPhase(schoolName) === null && (
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-1">
                Is this a primary or secondary school? <span className="text-red-500">*</span>
              </legend>
              <div className="space-y-2 mt-1">
                {SCHOOL_PHASE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center gap-3 p-2.5 border rounded-lg cursor-pointer transition-colors ${
                      schoolPhase === option.value
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="schoolPhase"
                      value={option.value}
                      checked={schoolPhase === option.value}
                      onChange={() => setSchoolPhase(option.value)}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <span className="text-sm text-gray-900">{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            )}

            {/* SENCO confirmation */}
            <label className="flex items-start gap-3 p-2.5 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={sencoConfirmed}
                onChange={(e) => setSencoConfirmed(e.target.checked)}
                className="h-4 w-4 mt-0.5 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <span className="text-sm text-gray-900">
                I confirm this person is the SENCO, or holds an equivalent role, at this school{' '}
                <span className="text-red-500">*</span>
              </span>
            </label>
          </div>

          {/* Error/Success messages */}
          {submitError && (
            <div className="mx-5 mb-3 p-3 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg">
              {submitError}
            </div>
          )}
          {submitSuccess && (
            <div className="mx-5 mb-3 p-3 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg">
              {submitSuccess}
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-between gap-3 p-5 pt-3 border-t border-gray-100">
            <Dialog.Close asChild>
              <button
                type="button"
                disabled={isSubmitting}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium disabled:opacity-50"
              >
                Cancel
              </button>
            </Dialog.Close>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
              className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center ${
                isFormValid && !isSubmitting
                  ? 'bg-primary text-white hover:bg-primary/90'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Registering...
                </>
              ) : (
                'Register SENCO'
              )}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
